// lurik-frontend/src/use-cases/authentication/useTwoFactorService.ts
import { useState } from 'react';
import { useTwoFactorApi } from '../../infra/two-factor-api/useTwoFactorApi';
import { useLocalStorageService } from '../local-storage';
import { useNavigationService } from '../navigation';
import { executeAlert } from "../../application/states/toast-alert"
import { ITwoFactorErrorTypes } from '../../application/models/ITwoFactor';
import { useAppDispatch } from '../../application/states/hooks';

export const useTwoFactorService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const twoFactorApi = useTwoFactorApi();
  const { setItem, getItem } = useLocalStorageService();
  const { navigateTo } = useNavigationService();
  const dispatch = useAppDispatch()

  const activateTwoFactor = async (userId: string) => {
    try {
      setIsLoading(true);
      setError(null);
          // ✅ ADICIONAR LOG para debug:

      const response = await twoFactorApi.activateTwoFactor({ userId });
      
      // Salvar dados temporários para verificação
      setItem({
        key: 'TWO_FACTOR_TEMP_DATA',
        value: JSON.stringify({
          userId,
          secret: response.secret,
          backupCodes: response.backupCodes
        })
      });
      
      dispatch(executeAlert({
        type: 'success',
        message: '2FA ativado com sucesso! Escaneie o QR Code.'
      }));
      
      return response;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.result?.message || 'Erro ao ativar 2FA';


      setError(errorMessage);
      dispatch(executeAlert({
        type: 'error',
        message: errorMessage
      }));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyTwoFactor = async (code: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const tempData = getItem({ key: 'TWO_FACTOR_TEMP_DATA' });
      if (!tempData) {
        throw new Error('Dados temporários não encontrados');
      }
      
      const { userId, secret } = JSON.parse(tempData);
      
      const response = await twoFactorApi.verifyTwoFactor({
        userId,
        code,
        secret
      });
      
      // Limpar dados temporários
      setItem({ key: 'TWO_FACTOR_TEMP_DATA', value: "" });
      
      dispatch(executeAlert({
        type: 'success',
        message: '2FA verificado com sucesso!'
      }));
      
      return response;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.result?.message || 'Erro ao verificar 2FA';
      setError(errorMessage);
      dispatch(executeAlert({
        type: 'error',
        message: errorMessage
      }));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const completeTwoFactorLogin = async (code: string, tempToken: string,  useBackupCode: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await twoFactorApi.completeTwoFactorLogin({
        code,        // ✅ CORRIGIR: code primeiro
        tempToken,   // ✅ CORRIGIR: tempToken segundo
        useBackupCode
      });
      
      // Salvar token JWT final
      setItem({
        key: 'ACCESS_TOKEN',
        value: response.token
      });
      
      dispatch(executeAlert({
        type: 'success',
        message: 'Login 2FA realizado com sucesso!'
      }));
      
      // Navegar para dashboard
      navigateTo({ route: '/workspace' });
      
      return response;
    } catch (err: any) {
      
      // ✅ CORRIGIR: Pegar a mensagem da estrutura correta
      const errorMessage = err?.message || err?.response?.data?.result?.message || 'Erro desconhecido';
      const errorType = err?.type || err?.response?.data?.result?.type || 'unknown';
      
      
      setError(errorMessage);
      
      
      dispatch(executeAlert({
        type: 'error',
        message: errorMessage
      }));
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deactivateTwoFactor = async (userId: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await twoFactorApi.deactivateTwoFactor({
        userId,
        password
      });
      
      dispatch(executeAlert({
        type: 'success',
        message: '2FA desativado com sucesso!'
      }));
      
      return response;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.result?.message || 'Erro ao desativar 2FA';
      setError(errorMessage);
      dispatch(executeAlert({
        type: 'error',
        message: errorMessage
      }));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const generateBackupCodes = async (userId: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await twoFactorApi.generateBackupCodes({
        userId,
        password
      });
      
      dispatch(executeAlert({
        type: 'success',
        message: 'Novos códigos de backup gerados!'
      }));
      
      return response;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.result?.message || 'Erro ao gerar códigos de backup';
      setError(errorMessage);
      dispatch(executeAlert({
        type: 'error',
        message: errorMessage
      }));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    activateTwoFactor,
    verifyTwoFactor,
    completeTwoFactorLogin,
    deactivateTwoFactor,
    generateBackupCodes
  };
};